import { useLayoutEffect } from 'react';
import { Link } from 'react-router';
import PlanComparison from '../components/PlanComparison';
import { useAuth } from '../context/AuthContext';
import { PRO_PRICE, PRO_PRICE_CURRENCY } from '../constants/pricing';
import { STATS_NAV_ITEM } from '../constants/navigation';

const LANDING_TITLE = 'EQ Your Ears – Audio Frequency Training | Ear Training Game';
const LANDING_DESCRIPTION =
  'Train your ears to identify frequencies with EQ Your Ears. Free interactive ear training for audio engineers, producers, and musicians. Practice with a 31-band EQ, spectrogram, and track your progress.';

function Landing() {
  const { isAuthenticated } = useAuth();

  // SEO: set page title and meta description when landing is mounted
  useLayoutEffect(() => {
    const prevTitle = document.title;
    document.title = LANDING_TITLE;

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevContent = metaDesc?.getAttribute('content') ?? '';
    if (metaDesc) {
      metaDesc.setAttribute('content', LANDING_DESCRIPTION);
    }

    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute('content', prevContent);
    };
  }, []);

  const statsHref = isAuthenticated ? STATS_NAV_ITEM.to : '/signup';
  const statsLinkLabel = isAuthenticated ? 'Open Stats' : 'See Pro';

  return (
    <article className="landing-page" aria-label="EQ Your Ears home">
      {/* JSON-LD for SEO (WebApplication) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'EQ Your Ears',
            description: LANDING_DESCRIPTION,
            applicationCategory: 'Game',
            isAccessibleForFree: true,
            offers: [
              {
                '@type': 'Offer',
                name: 'Free',
                price: '0',
                priceCurrency: PRO_PRICE_CURRENCY,
              },
              {
                '@type': 'Offer',
                name: 'Pro',
                priceSpecification: {
                  '@type': 'UnitPriceSpecification',
                  price: PRO_PRICE,
                  priceCurrency: PRO_PRICE_CURRENCY,
                  billingIncrement: 1,
                  unitCode: 'MON',
                },
              },
            ],
            featureList: [
              '31-band parametric EQ ear training',
              'Real-time spectrogram and frequency response',
              'Gamified frequency identification',
              'Progress tracking and statistics',
              'Feedback simulation and monitor ring-out practice',
            ],
          }),
        }}
      />

      {/* Waveform Banner */}
      <div className="landing-hero-waveform" aria-hidden="true" />

      {/* Gradient body: #000 at top flowing to bottom of page */}
      <div className="landing-gradient-body">
        {/* Hero Content */}
        <section className="landing-hero-content">
          <h1 className="landing-hero-title">
            EQ Your Ears: Precision Ear Training for Pros.
          </h1>
          <p className="landing-hero-tagline">
            Interactive frequency recognition for audio engineers, producers, and students.
            <br />
            Develop the listening skills to put you in the class of true professionals.
          </p>
          <Link
            to="/app"
            className="landing-cta-btn"
          >
            Open the Trainer
          </Link>
        </section>

        {/* Features Section */}
        <section className="landing-features-section" id="features" aria-labelledby="features-heading">
        <h2 id="features-heading" className="landing-features-heading">
          Trainer and Simulator are free. No download required.
        </h2>
        <p className="landing-features-subhead">
          Upgrade to Pro to track your stats and access the full frequency range.
        </p>
        <div className="landing-features-grid">
          <div className="landing-feature-card">
            <div className="landing-feature-card-img-wrapper">
              <div className="landing-feature-card-img-center">
                <img
                  src="/assets/eq-preview.png"
                  alt="Trainer preview showing the 31-band equalizer"
                  className="landing-feature-card-img"
                />
              </div>
            </div>
            <h3 className="landing-feature-card-title">Trainer</h3>
            <p className="landing-feature-card-desc">
              Designed like a 31 band eq, this tool builds direct muscle memory and confidence.

            </p>
            <Link to="/app" className="landing-feature-card-link">
              Open the Trainer
            </Link>
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-card-img-wrapper">
              <div className="landing-feature-card-img-center">
                <img
                  src="/assets/dashboard-preview.png"
                  alt="Stats preview showing frequency mastery statistics"
                  className="landing-feature-card-img"
                />
              </div>
            </div>
            <h3 className="landing-feature-card-title">Stats</h3>
            <p className="landing-feature-card-desc">
              Track your progress with detailed data visualisations. Visualise
              your frequency mastery over time. Pro only — recorded in Training mode.
            </p>
            {isAuthenticated ? (
              <Link to={statsHref} className="landing-feature-card-link">
                {statsLinkLabel}
              </Link>
            ) : (
              <a href={statsHref} className="landing-feature-card-link">
                {statsLinkLabel}
              </a>
            )}
          </div>
          <div className="landing-feature-card">
            <div className="landing-feature-card-img-wrapper">
              <div className="landing-feature-card-img-center">
                <img
                  src="/assets/simulator-preview.png"
                  alt="Simulator preview showing a monitor ring-out scenario"
                  className="landing-feature-card-img"
                />
              </div>
            </div>
            <h3 className="landing-feature-card-title">Simulator</h3>
            <p className="landing-feature-card-desc">
              Build gain, hear the room&apos;s natural resonances emerge, and ring
              out a monitor with the fewest cuts.
            </p>
            <Link to="/simulator" className="landing-feature-card-link">
              Open the Simulator
            </Link>
          </div>
        </div>
        </section>

        <section className="landing-plans-section" aria-label="Free and Pro comparison">
          <PlanComparison variant="dark" />
          <Link to="/signup" className="landing-plans-cta">
            Get full access
          </Link>
        </section>
      </div>
    </article>
  );
}

export default Landing;
